import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { Link } from 'react-router-dom'
import type { CompanyDashboardSummary, CompanyDashboardWeekDay } from '@/types/company.types'
import { ChartContainer, ChartTooltip, ChartTooltipContent, Panel } from '@/components/ui'
import type { ChartConfig } from '@/components/ui'
import { cn } from '@/lib/cn'

export interface WeekPanelProps {
  companyId: string
  summary?: CompanyDashboardSummary
  weekAttendance: CompanyDashboardWeekDay[]
  isLoading: boolean
}

/** Series color lives in the config so light/dark swap in one place. */
const chartConfig: ChartConfig = {
  presentPct: {
    label: 'Attendance',
    theme: { light: '#004a6b', dark: '#4aa3c4' },
  },
}

interface FooterStat {
  label: string
  value: string
  isZero: boolean
  title?: string
}

export function WeekPanel({
  companyId,
  summary,
  weekAttendance,
  isLoading,
}: WeekPanelProps): React.ReactElement {
  const footer: FooterStat[] = [
    {
      label: 'Hours worked',
      value: isLoading ? '—' : String(summary?.totalWorkedHoursWeek ?? 0),
      isZero: (summary?.totalWorkedHoursWeek ?? 0) === 0,
    },
    {
      label: 'Shifts scheduled',
      value: isLoading ? '—' : String(summary?.shiftsScheduledWeek ?? 0),
      isZero: (summary?.shiftsScheduledWeek ?? 0) === 0,
    },
    {
      label: 'Avg min past grace',
      title: 'Average minutes past the grace period, late arrivals only',
      value: isLoading ? '—' : `${summary?.avgLateMinutesWeek ?? 0}m`,
      isZero: (summary?.avgLateMinutesWeek ?? 0) === 0,
    },
  ]

  // null = a day still ahead or with nobody scheduled: drawn as a gap, not 0.
  const hasData = weekAttendance.some((d) => d.presentPct !== null)
  const srSummary = !hasData
    ? 'No day has attendance data yet this week.'
    : `Attendance by day: ${weekAttendance
        .map((d) => `${d.label} ${d.presentPct === null ? 'no data' : `${d.presentPct} percent`}`)
        .join(', ')}.`

  return (
    <Panel
      as="section"
      aria-labelledby="week-heading"
      title={
        <div>
          <h3
            id="week-heading"
            className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100"
          >
            This week
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Share of scheduled staff who attended, per day
          </p>
        </div>
      }
      action={
        <Link
          to={`/shifts?companyId=${companyId}`}
          className="text-xs font-medium text-primary underline-offset-4 hover:underline dark:text-sky-400"
        >
          Shift planning
        </Link>
      }
    >
      <p className="sr-only">{isLoading ? 'Loading weekly attendance.' : srSummary}</p>

      {isLoading ? (
        <div className="h-44 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700/50" aria-hidden />
      ) : !hasData ? (
        <div className="flex h-44 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-200 text-center dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            No day has data yet
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The trend fills in as scheduled shifts are worked this week.
          </p>
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="aspect-auto h-44 w-full">
          <AreaChart data={weekAttendance} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
            <defs>
              <linearGradient id="companyWeekAttendanceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-presentPct)" stopOpacity={0.28} />
                <stop offset="100%" stopColor="var(--color-presentPct)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 50, 100]}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `${v}%`}
              width={44}
            />
            <ChartTooltip
              filterNull={false}
              cursor={{ stroke: 'var(--color-presentPct)', strokeOpacity: 0.25, strokeWidth: 1 }}
              content={
                <ChartTooltipContent
                  formatter={(v) => (v === null || v === undefined ? 'No data' : `${v}%`)}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="presentPct"
              stroke="var(--color-presentPct)"
              strokeWidth={2}
              fill="url(#companyWeekAttendanceFill)"
              connectNulls={false}
              dot={{ r: 2.5, fill: 'var(--color-presentPct)', strokeWidth: 0 }}
              activeDot={{ r: 3.5, strokeWidth: 2, stroke: 'var(--color-presentPct)' }}
            />
          </AreaChart>
        </ChartContainer>
      )}

      <dl className="mt-4 grid grid-cols-3 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700/70">
        {footer.map((stat) => (
          <div key={stat.label} className="min-w-0">
            <dt className="truncate text-xs text-gray-500 dark:text-gray-400" title={stat.title}>
              {stat.label}
            </dt>
            <dd
              className={cn(
                'mt-1 text-lg font-semibold tabular-nums tracking-[-0.01em]',
                !isLoading && stat.isZero
                  ? 'text-gray-300 dark:text-gray-600'
                  : 'text-gray-900 dark:text-gray-100'
              )}
            >
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  )
}

WeekPanel.displayName = 'WeekPanel'
